import { useState } from "react";
import { format, subDays } from "date-fns";
import { Navigate } from "react-router-dom";
import { Routes } from "router";
import { DatePicker } from "theme";
import { Header } from "components";
import * as XLSX from "xlsx";

import { Button, useTheme, TableBody, TableCell, TableRow, Box } from "@mui/material";
import { 
  StyledTable, 
  StyledTableCell, 
  StyledTableContainer, 
  StyledTableHead 
} from "../../theme/components/Table/Table.tsx";

import DatepickerPopper from "../../theme/components/DatePicker/DatepickerPopper.tsx";
import { useAuth } from "../../utils/context/Auth/AuthContext.tsx";

const Reports = () => {
  const { user, token } = useAuth();
  const { palette } = useTheme();
  const [startDate, setStartDate] = useState<Date | null>(subDays(new Date(), 30));
  const [endDate, setEndDate] = useState<Date | null>(new Date());
  const [isDownloading, setIsDownloading] = useState(false);
  const [tableData, setTableData] = useState<Array<Array<any>>>([]);
  const [hasData, setHasData] = useState(false);
  const [sumaColumnIndex, setSumaColumnIndex] = useState<number>(-1);

  if (!user) return null;

  if (user.email !== import.meta.env.VITE_ADMIN_EMAIL) return <Navigate to={Routes.Root} />;

  const summaryLabels = ['Загальні надходження', 'Загальні витрати', 'Загальний прибуток'];
  
  const isSummaryRow = (row: Array<any>) => {
    const firstCell = String(row[0] || '').trim();
    return summaryLabels.includes(firstCell);
  };

  const formatCellValue = (cell: any, columnIndex: number) => {
    if (cell === null || cell === undefined || cell === '') {
      return '';
    }
    
    // Format currency for "Сума" column
    if (columnIndex === sumaColumnIndex && typeof cell === 'number') {
      return new Intl.NumberFormat('uk-UA', {
        style: 'currency',
        currency: 'UAH',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(cell);
    }
    
    // Check if it's a potential Excel date serial number
    if (typeof cell === 'number' && cell > 40000 && cell < 60000) {
      // Convert Excel serial date to JavaScript Date
      const date = XLSX.SSF.parse_date_code(cell);
      if (date) {
        // Format as DD-MM-YYYY
        const day = String(date.d).padStart(2, '0');
        const month = String(date.m).padStart(2, '0');
        const year = date.y;
        return `${day}-${month}-${year}`;
      }
    }
    
    return cell;
  };

  const handleViewReport = async () => {
    if (!startDate || !endDate || !token) return;

    setIsDownloading(true);

    try {
      const formattedStartDate = format(startDate, "yyyy-MM-dd'T'HH:mm:ss");
      const formattedEndDate = format(endDate, "yyyy-MM-dd'T'HH:mm:ss");
      const downloadUrl = `${import.meta.env.VITE_REACT_APP_SERVER_HOST}/admin/api/v1/reports/report/finance?startDate=${formattedStartDate}&endDate=${formattedEndDate}`;

      const response = await fetch(downloadUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch report');
      }

      const blob = await response.blob();
      
      // Parse Excel file
      const arrayBuffer = await blob.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });
      
      // Get first sheet
      const firstSheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[firstSheetName];
      
      // Convert to array of arrays
      const data = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as Array<Array<any>>;
      
      // Find and extract summary rows
      const summaryLabels = ['Загальні надходження', 'Загальні витрати', 'Загальний прибуток'];
      const summaryRows: Array<Array<any>> = [];
      const regularRows: Array<Array<any>> = [];
      
      data.slice(1).forEach(row => {
        const firstCell = String(row[0] || '').trim();
        if (summaryLabels.includes(firstCell)) {
          summaryRows.push(row);
        } else {
          regularRows.push(row);
        }
      });
      
      // Sort summary rows in the desired order
      summaryRows.sort((a, b) => {
        const indexA = summaryLabels.indexOf(String(a[0] || '').trim());
        const indexB = summaryLabels.indexOf(String(b[0] || '').trim());
        return indexA - indexB;
      });
      
      // Combine: header + summary rows + regular rows
      const reorderedData = [data[0], ...summaryRows, ...regularRows];
      
      // Find "Сума" column index
      const headerRow = data[0];
      const sumaIndex = headerRow.findIndex((cell: any) => 
        String(cell || '').trim().toLowerCase() === 'сума'
      );
      setSumaColumnIndex(sumaIndex);
      
      setTableData(reorderedData);
      setHasData(true);
    } catch (error) {
      console.error('Error fetching report:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDownloadReport = async () => {
    if (!startDate || !endDate || !token) return;

    setIsDownloading(true);

    try {
      const formattedStartDate = format(startDate, "yyyy-MM-dd'T'HH:mm:ss");
      const formattedEndDate = format(endDate, "yyyy-MM-dd'T'HH:mm:ss");
      const downloadUrl = `${import.meta.env.VITE_REACT_APP_SERVER_HOST}/admin/api/v1/reports/report/finance?startDate=${formattedStartDate}&endDate=${formattedEndDate}`;

      const response = await fetch(downloadUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to download report');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `finance-report-${format(startDate, "yyyy-MM-dd")}-to-${format(endDate, "yyyy-MM-dd")}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading report:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <>
      <Header />
      <div style={{
        width: "100%",
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden"
      }}>
        <div style={{
        padding: "16px",
        backgroundColor: palette.primary.main,
        borderBottom: `1px solid ${palette.secondary.light}`,
        display: "flex",
        alignItems: "center",
        gap: "16px",
        flexWrap: "wrap"
      }}>
        <DatePicker
          label="Дата початку"
          value={startDate}
          onChange={(newValue) => setStartDate(newValue)}
          slots={{
            popper: DatepickerPopper,
          }}
          slotProps={{
            textField: {
              sx: {
                width: { xs: "100%", sm: "280px" },
                minWidth: "200px"
              }
            }
          }}
        />
        <DatePicker
          label="Дата закінчення"
          value={endDate}
          onChange={(newValue) => setEndDate(newValue)}
          slots={{
            popper: DatepickerPopper,
          }}
          slotProps={{
            textField: {
              sx: {
                width: { xs: "100%", sm: "280px" },
                minWidth: "200px"
              }
            }
          }}
        />
        <Button
          variant="contained"
          onClick={handleViewReport}
          disabled={!startDate || !endDate || isDownloading}
          sx={{ minWidth: "150px" }}
        >
          {isDownloading ? "Завантаження..." : "Переглянути звіт"}
        </Button>
        <Button
          variant="outlined"
          onClick={handleDownloadReport}
          disabled={!startDate || !endDate || isDownloading}
          sx={{ minWidth: "150px" }}
        >
          Завантажити
        </Button>
      </div>
      <div style={{
        flex: 1,
        display: "flex",
        alignItems: hasData ? "flex-start" : "center",
        justifyContent: "center",
        padding: "16px",
        overflow: "auto"
      }}>
        {!hasData ? (
          <div style={{ textAlign: "center" }}>
            <p style={{ fontSize: "18px", color: palette.text.primary }}>
              Оберіть діапазон дат та натисніть "Переглянути звіт", щоб отримати фінансовий звіт.
            </p>
          </div>
        ) : (
          <Box sx={{ 
            width: "100%", 
            overflowX: "auto",
            maxWidth: "100%"
          }}>
            <StyledTableContainer sx={{ 
              maxHeight: "calc(100vh - 200px)",
              minWidth: { xs: "100%", sm: "600px" }
            }}>
              <StyledTable stickyHeader>
                <StyledTableHead>
                  <TableRow>
                    {tableData[0]?.map((cell, index) => (
                      <TableCell 
                        key={index}
                        sx={{ 
                          whiteSpace: 'nowrap',
                          position: 'sticky',
                          top: 0,
                          zIndex: 2
                        }}
                      >
                        {cell}
                      </TableCell>
                    ))}
                  </TableRow>
                </StyledTableHead>
                <TableBody>
                  {tableData.slice(1).map((row, rowIndex) => (
                    <TableRow key={rowIndex}>
                      {row.map((cell, cellIndex) => (
                        <StyledTableCell 
                          key={cellIndex}
                          sx={{ 
                            whiteSpace: 'nowrap',
                            fontWeight: isSummaryRow(row) ? 'bold' : 'normal',
                            backgroundColor: isSummaryRow(row) ? palette.background.accent : 'transparent'
                          }}
                        >
                          {formatCellValue(cell, cellIndex)}
                        </StyledTableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </StyledTable>
            </StyledTableContainer>
          </Box>
        )}
      </div>
      </div>
    </>
  );
}

export default Reports;
